import React, { useState } from 'react';
import {
    AccountCard,
    AccountGrid,
    AccountSection,
    SectionBadge,
    dangerButtonClassName,
    inputClassName,
    labelClassName,
    primaryButtonClassName,
} from './AccountUI';

const Toggle = ({ enabled, onChange, label, description }) => (
    <div className="flex items-start justify-between gap-4 rounded-3xl border border-blue-100 bg-blue-50/40 px-4 py-4">
        <div>
            <p className="text-sm font-medium text-slate-950">{label}</p>
            <p className="mt-1 text-sm leading-6 text-slate-500">{description}</p>
        </div>
        <button
            type="button"
            onClick={onChange}
            className={`relative mt-1 inline-flex h-7 w-12 items-center rounded-full transition ${enabled ? 'bg-blue-600' : 'bg-slate-300'}`}
        >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
    </div>
);

export default function Settings() {
    const [workspaceName, setWorkspaceName] = useState('Acme Corporation');
    const [subdomain, setSubdomain] = useState('acme');
    const [notifications, setNotifications] = useState({
        newLeads: true,
        campaignUpdates: true,
        weeklyReports: false,
    });
    const [theme, setTheme] = useState('light');
    const [isSaving, setIsSaving] = useState(false);

    const handleSave = () => {
        setIsSaving(true);
        setTimeout(() => setIsSaving(false), 1200);
    };

    return (
        <div className="p-6 sm:p-8 lg:p-10">
            <AccountSection
                eyebrow="Workspace"
                title="Settings that feel operational, not noisy."
                subtitle="Keep workspace defaults, notification behavior, and visual preferences organized in a single calm system."
                actions={<SectionBadge tone="blue">Workspace controls</SectionBadge>}
            >
                <AccountGrid>
                    <AccountCard className="xl:col-span-7">
                        <div className="space-y-5">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-950">Workspace details</h3>
                                <p className="mt-2 text-sm leading-6 text-slate-500">The basics people see first when collaborating inside your account.</p>
                            </div>

                            <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                                <label className="block">
                                    <span className={labelClassName}>Workspace name</span>
                                    <input type="text" value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} className={inputClassName} />
                                </label>
                                <label className="block">
                                    <span className={labelClassName}>Subdomain</span>
                                    <div className="relative">
                                        <input type="text" value={subdomain} onChange={(event) => setSubdomain(event.target.value)} className={`${inputClassName} pr-28`} />
                                        <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-sm text-slate-400">.outreach.app</span>
                                    </div>
                                </label>
                            </div>

                            <button type="button" onClick={handleSave} disabled={isSaving} className={primaryButtonClassName}>
                                {isSaving ? 'Saving...' : 'Save workspace'}
                            </button>
                        </div>
                    </AccountCard>

                    <AccountCard className="xl:col-span-5">
                        <div className="space-y-5">
                            <div>
                                <h3 className="text-lg font-semibold text-slate-950">Appearance</h3>
                                <p className="mt-2 text-sm leading-6 text-slate-500">Choose the visual mode that should guide the account experience.</p>
                            </div>

                            <div className="grid grid-cols-1 gap-3">
                                {['light', 'dark'].map((mode) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        onClick={() => setTheme(mode)}
                                        className={`rounded-3xl border px-5 py-5 text-left transition ${theme === mode ? 'border-blue-600 bg-blue-600 text-white' : 'border-blue-100 bg-blue-50/40 text-slate-700 hover:bg-white'}`}
                                    >
                                        <p className="text-sm font-semibold capitalize">{mode} mode</p>
                                        <p className={`mt-2 text-sm ${theme === mode ? 'text-blue-100' : 'text-slate-500'}`}>
                                            {mode === 'light' ? 'Clean surfaces and a brighter workspace.' : 'Reduced glare with a darker shell.'}
                                        </p>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </AccountCard>
                </AccountGrid>

                <AccountCard>
                    <div className="space-y-5">
                        <div>
                            <h3 className="text-lg font-semibold text-slate-950">Notifications</h3>
                            <p className="mt-2 text-sm leading-6 text-slate-500">Only keep the updates that help you move faster.</p>
                        </div>
                        <div className="space-y-3">
                            <Toggle label="New leads" description="Send a notification when a new lead enters the workspace." enabled={notifications.newLeads} onChange={() => setNotifications((prev) => ({ ...prev, newLeads: !prev.newLeads }))} />
                            <Toggle label="Campaign updates" description="Highlight milestone changes and delivery events." enabled={notifications.campaignUpdates} onChange={() => setNotifications((prev) => ({ ...prev, campaignUpdates: !prev.campaignUpdates }))} />
                            <Toggle label="Weekly reports" description="Share a compact weekly summary every Monday." enabled={notifications.weeklyReports} onChange={() => setNotifications((prev) => ({ ...prev, weeklyReports: !prev.weeklyReports }))} />
                        </div>
                    </div>
                </AccountCard>

                <AccountCard className="border-red-100 bg-red-50/60">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                        <div>
                            <h3 className="text-lg font-semibold text-red-700">Danger zone</h3>
                            <p className="mt-2 max-w-2xl text-sm leading-6 text-red-600">
                                Permanently deleting the workspace removes its data, connected history, and account-level configuration.
                            </p>
                        </div>
                        <button type="button" className={dangerButtonClassName}>
                            Delete workspace
                        </button>
                    </div>
                </AccountCard>
            </AccountSection>
        </div>
    );
}
